import { MemoryStore, type OutboxEntry, type OutboxStore } from './outbox';

const DB_NAME = 'kinta-offline';
const STORE = 'outbox';

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: 'id' });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
  });
}

function wrap<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexedDB request failed'));
  });
}

/**
 * Outbox rows in IndexedDB so they survive reloads and closed tabs. Where IndexedDB is unavailable (some private
 * windows) it degrades to memory: queued writes then last only until the tab closes, which beats failing outright.
 */
export class IdbStore implements OutboxStore {
  private db: Promise<IDBDatabase | null> | undefined;
  private memory = new MemoryStore();

  private handle(): Promise<IDBDatabase | null> {
    this.db ??= typeof indexedDB === 'undefined' ? Promise.resolve(null) : open().catch(() => null);
    return this.db;
  }

  private async run<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T | undefined> {
    const db = await this.handle();
    return db ? wrap(fn(db.transaction(STORE, mode).objectStore(STORE))) : undefined;
  }

  async all(): Promise<OutboxEntry[]> {
    return (await this.run('readonly', (s) => s.getAll() as IDBRequest<OutboxEntry[]>)) ?? this.memory.all();
  }
  async put(entry: OutboxEntry): Promise<void> {
    if ((await this.run('readwrite', (s) => s.put(entry))) === undefined) await this.memory.put(entry);
  }
  async remove(id: string): Promise<void> {
    if ((await this.run('readwrite', (s) => s.delete(id))) === undefined && !(await this.handle())) await this.memory.remove(id);
  }
  async clear(): Promise<void> {
    await this.run('readwrite', (s) => s.clear());
    await this.memory.clear();
  }
}
